import { useCallback, useMemo, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Stack, Typography } from '@mui/material';
import EnvironmentGuard from 'src/components/EnvironmentGuard';
import ViewByToggle from 'src/components/ViewByToggle';
import { readFilterParams, useFilterParams } from 'src/hooks/filterParams';
import { useInfiniteAppPortfolio } from 'src/hooks/wdac';
import PortfolioTable from './PortfolioTable';
import PortfolioVersionsTable from './PortfolioVersionsTable';
import {
  APP_PORTFOLIO_PAGE_SIZE,
  DEFAULT_PORTFOLIO_FILTERS,
  DEFAULT_PORTFOLIO_SORT,
  PORTFOLIO_FILTER_OPTIONS,
} from './constants';

const VIEW_OPTIONS = [
  { value: 'apps', label: 'Applications', icon: 'mdi:application-outline' },
  { value: 'versions', label: 'Versions', icon: 'mdi:format-list-bulleted' },
];
const VIEWS = VIEW_OPTIONS.map(option => option.value);
const VIEW_TITLES = { apps: 'Applications', versions: 'Application Versions' };
const DEFAULT_VIEW = { view: VIEWS[0] };

const AppPortfolio = () => {
  const [view, setView] = useState(() => {
    const stored = readFilterParams(DEFAULT_VIEW)?.view;
    return VIEWS.includes(stored) ? stored : DEFAULT_VIEW.view;
  });
  const [filters, setFilters] = useState(
    () =>
      readFilterParams(DEFAULT_PORTFOLIO_FILTERS, PORTFOLIO_FILTER_OPTIONS) ??
      DEFAULT_PORTFOLIO_FILTERS,
  );
  // Search carries across the views like the filters, but stays out of the URL as free text.
  const [searchValue, setSearchValue] = useState('');
  const [sort, setSort] = useState(DEFAULT_PORTFOLIO_SORT);

  // Each view mounts its own action bar, so the page records the view rather than either bar.
  const viewParams = useMemo(() => ({ view }), [view]);

  useFilterParams(filters);
  useFilterParams(viewParams);

  const onFilterChange = useCallback(
    next => setFilters({ statuses: next.statuses ?? [], sources: next.sources ?? [] }),
    [],
  );

  const portfolioQuery = useInfiniteAppPortfolio({
    search: searchValue.trim(),
    statuses: filters.statuses,
    sources: filters.sources,
    sort,
    limit: APP_PORTFOLIO_PAGE_SIZE,
    enabled: view === 'apps',
  });

  const results = useMemo(
    () => portfolioQuery.data?.pages?.flatMap(page => page.results || []) || [],
    [portfolioQuery.data],
  );

  const totalCount = portfolioQuery.data?.pages?.[0]?.totalCount || 0;

  const loadMore = useCallback(() => {
    if (portfolioQuery.hasNextPage) {
      portfolioQuery.fetchNextPage();
    }
  }, [portfolioQuery]);

  const [sentryRef] = useInfiniteScroll({
    loading: portfolioQuery.isFetchingNextPage,
    hasNextPage: portfolioQuery.hasNextPage,
    disabled: view !== 'apps',
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  return (
    <EnvironmentGuard>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 1 }}>
        <Typography
          sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.4 }}>
          {VIEW_TITLES[view]}
        </Typography>
        <ViewByToggle value={view} options={VIEW_OPTIONS} onChange={setView} />
      </Stack>

      {view === 'apps' ? (
        <PortfolioTable
          isLoading={portfolioQuery.isLoading}
          results={results}
          sentryRef={sentryRef}
          isFetchingNextPage={portfolioQuery.isFetchingNextPage}
          totalCount={totalCount}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          filters={filters}
          onFilterChange={onFilterChange}
          sort={sort}
          onSortChange={setSort}
        />
      ) : (
        <PortfolioVersionsTable
          filters={filters}
          onFilterChange={onFilterChange}
          search={searchValue}
          onSearchChange={setSearchValue}
        />
      )}
    </EnvironmentGuard>
  );
};

export default AppPortfolio;
